import { ReactNode } from 'react';

import { Text } from '@calm-web/design-system';

import { AccountScreenConfig } from '@/components/SimplifiedSignup/Account/types';
import ControlScreenConfigs from '@/components/SimplifiedSignup/configs/Control/Screens';
import { ErrorScreenConfig } from '@/components/SimplifiedSignup/Error/types';
import { HandoffScreenConfig } from '@/components/SimplifiedSignup/Handoff/types';
import { paymentValueProps } from '@/components/SimplifiedSignup/Payment/data';
import { PaymentScreenConfig } from '@/components/SimplifiedSignup/Payment/types';
import { cannotExtendSubscription } from '@/components/SubscriptionExtension/util';

import messages from './messages';

const SubscriptionExtensionAccountScreenConfig: AccountScreenConfig = {
	...ControlScreenConfigs.account,
	hasPremiumSubCallback: undefined,
	backgroundKey: 'gray0',
	showHeader: false,
	heading: () => {
		return {
			message: messages.accountHeading,
			placeholders: {
				blue: (...chunks: ReactNode[]) => <Text color="blue3">{chunks}</Text>,
			},
		};
	},
	subheading: messages.accountSubHeading,
	limitedTimeOffer: true,
	hideValueProps: true,
	hideAuthModeToggle: true,
	FAQs: [
		{
			trigger: messages.accountFaqQuestion1,
			children: messages.accountFaqResponse1,
			id: 1,
		},
		{
			trigger: messages.accountFaqQuestion2,
			children: messages.accountFaqResponse2,
			id: 2,
		},
	],
	nextScreen: ({ routeToScreen, user, setError }) => {
		if (cannotExtendSubscription(user)) {
			setError({
				onScreenViewed: logEvent => {
					logEvent({
						eventName: 'Screen : Viewed',
						eventProps: {
							screen: 'Ineligible',
						},
					});
				},
				heading: messages.ineligibleErrorHeading,
				subheading: messages.ineligibleErrorSubheading,
				cta: {
					label: messages.ineligibleErrorCta,
					onClick: async (routerPush, logEvent) => {
						logEvent({
							eventName: 'Subscribe : Purchase : Form : Ineligible CTA : Clicked',
							eventProps: {
								CTA: messages.ineligibleErrorCta.defaultMessage,
							},
						});
						await routerPush('/app');
					},
				},
			});
		} else {
			routeToScreen('payment');
		}
	},
};

const SubscriptionExtensionPaymentScreenConfig: PaymentScreenConfig = {
	...ControlScreenConfigs.payment,
	heading: () => ({
		message: messages.paymentValuePropHeader,
		placeholders: {
			blue: (...chunks: ReactNode[]) => <Text color="blue3">{chunks}</Text>,
		},
	}),
	showToggle: false,
	terms: messages.terms,
	offerTerms: messages.offerTerms,
	nextScreen: ({ routeToScreen, user }) => {
		// The user has already a valid subscription that has been cancelled.
		// In this flow, the user renews his subscription, so in order to move to the
		// following screen, we need to check whether the subscription is no longer cancelled.
		if (user?.subscription?.valid && !user.subscription.is_canceled) {
			routeToScreen('handoff');
		}
	},
	allowChurned: true,
	upsellBullets: paymentValueProps,
	premiumLineHeader: undefined,
	hidePaypal: true,
	userCanExit: false,
	eventProps: {
		extension_type: 'annual_plan_extension',
	},
	endpoint: 'subscription/renew',
	ctaCopy: messages.paymentCta,
	isRenewalOffer: true,
};

const SubscriptionExtensionHandoffScreenConfig: HandoffScreenConfig = {
	...ControlScreenConfigs.handoff,
	heading: () => messages.handoffHeading,
	subHeading: messages.handoffSubHeading,
	hideQRCode: { mobile: true },
	userCanExit: true,
};

const SubscriptionExtensionErrorScreenConfig: ErrorScreenConfig = {
	...ControlScreenConfigs.error,
};

export default {
	account: SubscriptionExtensionAccountScreenConfig,
	payment: SubscriptionExtensionPaymentScreenConfig,
	handoff: SubscriptionExtensionHandoffScreenConfig,
	error: SubscriptionExtensionErrorScreenConfig,
};
